// components/FormBuilder/ScreenEditor.jsx
import React, { useState } from 'react';
import { Card, Input, Space, Form } from 'antd';
import AddContent from './AddContentButton';
import { TextBody, TextCaption, TextHeading, TextSubheading } from './ComponentTypes/Text';
import { DatePicker, TextArea, TextInput } from './ComponentTypes/Input';
import { Typography } from 'antd';
import { SortableContainer, SortableElement, arrayMove } from 'react-sortable-hoc';
import { FooterButton } from './ComponentTypes/FooterButton';
import { transformString } from '../../functions';
import { CheckboxGroup, Dropdown, OptIn, RadioButtonsGroup } from './ComponentTypes/selection';
import { Image } from './ComponentTypes/Media';

const renderComponent = (component, onChange, onDelete) => {
    switch (component.type) {
        case 'TextHeading':
            return <TextHeading key={component.id} component={component} onDelete={onDelete} onChange={onChange} />;

        case 'TextSubheading':
            return <TextSubheading key={component.id} component={component} onChange={onChange} onDelete={onDelete} />;
        case 'TextBody':
            return <TextBody key={component.id} component={component} onChange={onChange} onDelete={onDelete} />;
        case 'TextCaption':
            return <TextCaption key={component.id} component={component} onChange={onChange} onDelete={onDelete} />;
        case 'TextInput':
            return <TextInput key={component.id} component={component} onChange={onChange} onDelete={onDelete} />;
        case 'TextArea':
            return <TextArea key={component.id} component={component} onChange={onChange} onDelete={onDelete} />;
        case 'DatePicker':
            return <DatePicker key={component.id} component={component} onChange={onChange} onDelete={onDelete} />;
        case 'RadioButtonsGroup':
            return (
                <RadioButtonsGroup key={component.id} component={component} onChange={onChange} onDelete={onDelete} />
            );
        case 'CheckboxGroup':
            return <CheckboxGroup key={component.id} component={component} onChange={onChange} onDelete={onDelete} />;
        case 'Dropdown':
            return <Dropdown key={component.id} component={component} onChange={onChange} onDelete={onDelete} />;
        case 'OptIn':
            return <OptIn key={component.id} component={component} onChange={onChange} onDelete={onDelete} />;
        case 'Image':
            return <Image key={component.id} component={component} onChange={onChange} onDelete={onDelete} />;
        // case 'single-choice':
        //     return (
        //         <SingleChoiceComponent
        //             key={component.id}
        //             content={component.content}
        //             onChange={onChange}
        //             onDelete={onDelete}
        //         />
        //     );
        // case 'multiple-choice':
        //     return (
        //         <MultipleChoiceComponent
        //             key={component.id}
        //             content={component.content}
        //             onChange={onChange}
        //             onDelete={onDelete}
        //         />
        //     );
        default:
            return null;
    }
};
const SortableItem = SortableElement(({ component, onDelete, onChange, key }) => (
    <div key={key}>{renderComponent(component, onChange, onDelete)}</div>
));
const SortableList = SortableContainer(({ items, onDelete, onChange }) => {
    return (
        <div>
            <Space direction="vertical" style={{ width: '100%' }} size="large">
                {items.map((component, index) => (
                    <SortableItem
                        key={`component-item-${index}`}
                        index={index}
                        component={component}
                        onDelete={onDelete}
                        onChange={onChange}
                    />
                ))}
            </Space>
        </div>
    );
});

const ScreenEditor = ({ screen, onScreenUpdate }) => {
    const [isSaving, setIsSaving] = useState(false);

    const handleTitleChange = (newTitle) => {
        onScreenUpdate({
            ...screen,
            title: newTitle,
        });
    };

    const handleAddComponent = (componentType, subType) => {
        const newComponent = {
            type: componentType,
            id: `${componentType}_${screen.components.length + 1}`,
        };

        switch (componentType) {
            case 'TextHeading':
                newComponent.text = 'Text';
                break;
            case 'TextSubheading':
                newComponent.text = 'Text';
                break;
            case 'TextBody':
                newComponent.text = 'Text';
                break;
            case 'TextCaption':
                newComponent.text = 'Text';
                break;
            case 'TextInput':
                newComponent.label = 'Label';
                newComponent['input-type'] = 'text';
                newComponent.required = false;
                // newComponent.helperText = 'Instructions';
                break;
            case 'TextArea':
                newComponent.label = 'Label';
                newComponent.required = false;
                break;
            case 'DatePicker':
                newComponent.label = 'Label';
                newComponent.name = transformString('DatePicker_', 4);
                break;
            case 'RadioButtonsGroup':
                newComponent.name = transformString('RadioButtonsGroup_', 4);
                newComponent.label = 'Label';
                newComponent['data-source'] = [
                    {
                        id: 'Option 1',
                        title: 'Option 1',
                    },
                    {
                        id: 'Option 2',
                        title: 'Option 2',
                    },
                ];
                break;
            case 'CheckboxGroup':
                newComponent.name = transformString('CheckboxGroup_', 4);
                newComponent.label = 'Label';
                newComponent['data-source'] = [
                    {
                        id: 'Option 1',
                        title: 'Option 1',
                    },
                    {
                        id: 'Option 2',
                        title: 'Option 2',
                    },
                ];
                break;
            case 'Dropdown':
                newComponent.name = transformString('Dropdown_', 4);
                newComponent.label = 'Label';
                newComponent['data-source'] = [
                    {
                        id: 'Option 1',
                        title: 'Option 1',
                    },
                    {
                        id: 'Option 2',
                        title: 'Option 2',
                    },
                ];
                break;
            case 'Image':
                newComponent.src = '';
                newComponent['scale-type'] = 'contain';
                newComponent['height'] = 400;
                break;
            default:
                break;
        }

        onScreenUpdate({
            ...screen,
            components: [...(screen.components || []), newComponent],
        });
    };

    const handleComponentUpdate = (componentId, updatedComponent) => {
        onScreenUpdate({
            ...screen,
            components: screen.components.map((comp) => (comp.id === componentId ? updatedComponent : comp)),
        });
    };

    const handleDeleteComponent = (componentId) => {
        onScreenUpdate({
            ...screen,
            components: screen.components.filter((comp) => comp.id !== componentId),
        });
    };

    const onSortEnd = ({ oldIndex, newIndex }) => {
        onScreenUpdate({
            ...screen,
            components: arrayMove(screen.components, oldIndex, newIndex),
        });
    };

    return (
        <Card
            style={{
                marginBottom: '1rem',
            }}
            title={
                <Space direction="vertical" style={{ width: '100%' }}>
                    <Typography.Title level={5}>Edit Content</Typography.Title>
                    <Input
                        value={screen.title}
                        onChange={(e) => handleTitleChange(e.target.value)}
                        placeholder="Screen Title"
                    />
                </Space>
            }
        >
            <SortableList
                items={screen.components}
                onSortEnd={onSortEnd}
                onDelete={(componentId) => handleDeleteComponent(componentId)}
                onChange={(component) => handleComponentUpdate(component.id, component)}
                useDragHandle
            />
            <AddContent onAdd={handleAddComponent} />
            <FooterButton
                component={screen.footerButton}
                onChange={(component) => onScreenUpdate({ ...screen, footerButton: component })}
            />
        </Card>
    );
};

export default ScreenEditor;
